import React, { useEffect, useState } from 'react';
import { Drawer, Form, Input, InputNumber, Button, Select, Modal, message } from 'antd';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import TestRuleModal, { RuleConditions } from './TestRuleModal';
import {
  CREATE_ADDRESS_KEYWORD_RULE,
  CreateAddressKeywordRuleInput,
  STATUS_ENABLED,
} from '../graphql/addressKeywordRuleGraphql';

interface RuleFormDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ZIP5 = /^\d{5}$/;
const DIGITS_ONLY = /^[\d\s]+$/;

/**
 * 关键词纠错规则只允许新建，不允许编辑（修改 = 删除后重新新建并重新测试）。
 * 保存门控：当前表单条件签名必须与测试通过时的签名一致。
 * 替换后关键词允许为空（= 删除该关键词），保存前弹窗二次确认。
 */
const RuleFormDrawer: React.FC<RuleFormDrawerProps> = ({ visible, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();

  const [testModalVisible, setTestModalVisible] = useState(false);
  // 测试通过时记录当时的规则条件签名与测试快照；当前条件签名与之一致才允许保存
  const [passed, setPassed] = useState<{ signature: string; testAddress: string; testResult: string } | null>(null);

  const keywordFrom = Form.useWatch('keywordFrom', form);
  const keywordTo = Form.useWatch('keywordTo', form);
  const state = Form.useWatch('state', form);
  const city = Form.useWatch('city', form);
  const zipCodes = Form.useWatch<string[] | undefined>('zipCodes', form);

  const conditions: RuleConditions = {
    keywordFrom: keywordFrom?.trim() || '',
    keywordTo: keywordTo?.trim() || '',
    state: state?.trim() || null,
    city: city?.trim() || null,
    zipCode: zipCodes && zipCodes.length > 0 ? zipCodes.map((z) => z.trim()).join(',') : null,
  };
  const conditionSignature = JSON.stringify(conditions);
  const testPassed = passed !== null && passed.signature === conditionSignature;

  useEffect(() => {
    if (!visible) {
      return;
    }
    form.resetFields();
    form.setFieldsValue({ priority: 100, status: STATUS_ENABLED, zipCodes: [] });
    setTestModalVisible(false);
    setPassed(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const [createRule, { loading: submitting }] = useMutation(CREATE_ADDRESS_KEYWORD_RULE);

  const validateConditions = (): boolean => {
    if (!conditions.keywordFrom) {
      message.warning(t('mapTool.addressKeywordRule.drawer.warnKeywordRequired') as string);
      return false;
    }
    if (DIGITS_ONLY.test(conditions.keywordFrom)) {
      message.warning(t('mapTool.addressKeywordRule.drawer.warnKeywordDigitsOnly') as string);
      return false;
    }
    if (conditions.keywordFrom.toLowerCase() === conditions.keywordTo.toLowerCase()) {
      message.warning(t('mapTool.addressKeywordRule.drawer.warnKeywordSame') as string);
      return false;
    }
    const invalidZip = (zipCodes ?? []).find((z) => !ZIP5.test(z.trim()));
    if (invalidZip) {
      message.warning(`${t('mapTool.addressKeywordRule.drawer.warnZipFormat')}: ${invalidZip}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    let values: Record<string, unknown>;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!validateConditions()) {
      return;
    }
    if (!testPassed || !passed) {
      message.warning(t('mapTool.addressKeywordRule.drawer.warnTestFirst') as string);
      return;
    }
    // 替换后为空 = 从地址中删除该关键词，影响面大，保存前二次确认
    if (!conditions.keywordTo) {
      Modal.confirm({
        title: t('mapTool.addressKeywordRule.drawer.confirmEmptyTitle') as string,
        content: t('mapTool.addressKeywordRule.drawer.confirmEmptyContent', { keyword: conditions.keywordFrom }) as string,
        okText: t('components.button.confirm') as string,
        cancelText: t('components.button.cancel') as string,
        onOk: () => doSubmit(values, passed),
      });
      return;
    }
    await doSubmit(values, passed);
  };

  const doSubmit = async (
    values: Record<string, unknown>,
    snapshot: { testAddress: string; testResult: string }
  ) => {
    const input: CreateAddressKeywordRuleInput = {
      priority: Number(values.priority),
      status: Number(values.status),
      keywordFrom: conditions.keywordFrom,
      keywordTo: conditions.keywordTo,
      state: conditions.state,
      city: conditions.city,
      zipCode: conditions.zipCode,
      testAddress: snapshot.testAddress,
      testResult: snapshot.testResult,
    };
    try {
      await createRule({ variables: { input } });
      message.success(t('mapTool.addressKeywordRule.drawer.messageAddSuccess') as string);
      onSuccess();
      onClose();
    } catch (err) {
      message.error((err as Error).message);
    }
  };

  const handleOpenTest = () => {
    if (!validateConditions()) {
      return;
    }
    setTestModalVisible(true);
  };

  return (
    <Drawer
      title={t('mapTool.addressKeywordRule.drawer.title') as string}
      width={600}
      open={visible}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('components.button.cancel')}</Button>
          <Button type="primary" loading={submitting} disabled={!testPassed} onClick={handleSubmit}>
            {t('components.button.save')}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="keywordFrom"
          label={t('mapTool.addressKeywordRule.drawer.labelKeywordFrom')}
          extra={t('mapTool.addressKeywordRule.drawer.hintKeywordFrom')}
          rules={[{ required: true, message: t('mapTool.addressKeywordRule.drawer.ruleKeywordFromRequired') as string }]}
        >
          <Input maxLength={128} placeholder={t('mapTool.addressKeywordRule.drawer.placeholderKeywordFrom') as string} />
        </Form.Item>
        <Form.Item
          name="keywordTo"
          label={t('mapTool.addressKeywordRule.drawer.labelKeywordTo')}
          extra={t('mapTool.addressKeywordRule.drawer.hintKeywordTo')}
        >
          <Input maxLength={128} placeholder={t('mapTool.addressKeywordRule.drawer.placeholderKeywordTo') as string} />
        </Form.Item>
        <Form.Item
          name="zipCodes"
          label={t('mapTool.addressKeywordRule.drawer.labelZipCode')}
          extra={t('mapTool.addressKeywordRule.drawer.hintScope')}
        >
          <Select
            mode="tags"
            tokenSeparators={[',', ' ', '，']}
            placeholder={t('mapTool.addressKeywordRule.drawer.placeholderZipCode') as string}
            open={false}
          />
        </Form.Item>
        <Form.Item name="city" label={t('mapTool.addressKeywordRule.drawer.labelCity')}>
          <Input placeholder={t('mapTool.addressKeywordRule.drawer.placeholderCommaSeparated') as string} maxLength={255} />
        </Form.Item>
        <Form.Item name="state" label={t('mapTool.addressKeywordRule.drawer.labelState')}>
          <Input placeholder={t('mapTool.addressKeywordRule.drawer.placeholderState') as string} maxLength={255} />
        </Form.Item>
        <Form.Item
          name="priority"
          label={t('mapTool.addressKeywordRule.drawer.labelPriority')}
          extra={t('mapTool.addressKeywordRule.drawer.hintPriority')}
          rules={[{ required: true, message: t('mapTool.addressKeywordRule.drawer.rulePriorityRequired') as string }]}
        >
          <InputNumber min={0} className="w-full" />
        </Form.Item>
        {/* 状态默认启用，不支持手工选择，仅作隐藏字段提交 */}
        <Form.Item name="status" hidden initialValue={STATUS_ENABLED}>
          <Input type="hidden" />
        </Form.Item>

        <div className="mb-2">
          <Button onClick={handleOpenTest}>
            {t('mapTool.addressKeywordRule.drawer.buttonTest')}
          </Button>
          <span className={`ml-3 text-sm ${testPassed ? 'text-green-600' : 'text-orange-500'}`}>
            {testPassed
              ? t('mapTool.addressKeywordRule.drawer.testPassed')
              : t('mapTool.addressKeywordRule.drawer.testRequired')}
          </span>
        </div>
        {testPassed && passed && (
          <div className="mt-2 px-3 py-2 rounded text-sm bg-green-50 text-green-700 border border-green-200">
            <div>{`${t('mapTool.addressKeywordRule.drawer.labelTestAddress')}: ${passed.testAddress}`}</div>
            <div>{`${t('mapTool.addressKeywordRule.drawer.labelTestResult')}: ${passed.testResult}`}</div>
          </div>
        )}
      </Form>

      <TestRuleModal
        visible={testModalVisible}
        conditions={conditions}
        onClose={() => setTestModalVisible(false)}
        onPass={(testAddress, testResult) => setPassed({ signature: conditionSignature, testAddress, testResult })}
      />
    </Drawer>
  );
};

export default RuleFormDrawer;
